import React, { useEffect, useRef } from 'react';
import { Heart } from 'lucide-react';

interface HeroFarewellSectionProps {
  content: any;
  language: 'en' | 'mr';
}

export const HeroFarewellSection: React.FC<HeroFarewellSectionProps> = ({ content, language }) => {
  const photoRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!photoRef.current) return;
      const rect = photoRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;

      photoRef.current.style.transform = `
        perspective(1000px)
        rotateX(${y * 8}deg)
        rotateY(${x * 8}deg)
        scale(1.03)
      `;
    };

    const handleMouseLeave = () => {
      if (photoRef.current) {
        photoRef.current.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale(1)';
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const name = language === 'mr' ? content.person.nameMr : content.person.name;
  const destination = language === 'mr' ? content.person.destinationMr : content.person.destination;
  const message = language === 'mr' ? content.event.messageMr : content.event.message;

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden">
      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-80 h-80 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-40 right-10 w-80 h-80 bg-pink-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10 max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        {/* Photo - 3D Transform */}
        <div className="flex justify-center perspective">
          <div className="relative w-full aspect-square max-w-md">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-pink-500 rounded-3xl blur-2xl opacity-75 animate-pulse" />
            <img
              ref={photoRef}
              src={content.person.photo}
              alt={content.person.name}
              className="relative w-full h-full object-cover rounded-3xl shadow-2xl transition-transform duration-300 border-4 border-blue-400"
            />
            <div className="absolute -bottom-4 -right-4 bg-pink-500 rounded-full p-4 shadow-lg">
              <Heart size={24} className="text-white fill-white" />
            </div>
          </div>
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-6">
          <div className="space-y-4">
            <p className="text-lg text-blue-300 font-semibold uppercase tracking-widest">
              {language === 'mr' ? 'विदा पर्व' : '✈️ Farewell Journey'}
            </p>
            <h1 className="text-6xl md:text-7xl font-black text-white drop-shadow-lg">
              {name}
            </h1>
            <p className="text-2xl md:text-3xl font-bold text-pink-300 italic">
              "{destination}"
            </p>
          </div>

          <p className="text-lg text-gray-300 leading-relaxed">
            {message}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 pt-6">
            <button className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-bold rounded-full hover:shadow-lg hover:shadow-blue-500/50 transition-all transform hover:scale-110">
              {language === 'mr' ? '💌 संदेश पाठवा' : '💌 Send Message'}
            </button>
            <button className="px-8 py-4 bg-pink-600 text-white font-bold rounded-full hover:shadow-lg hover:shadow-pink-500/50 transition-all transform hover:scale-110">
              {language === 'mr' ? '🎉 RSVP करा' : '🎉 RSVP'}
            </button>
          </div>

          {/* Info Badge */}
          <div className="flex gap-3 pt-4">
            <div className="px-4 py-2 bg-blue-900/50 border border-blue-400 rounded-full text-sm text-blue-300 font-semibold">
              📅 {language === 'mr' ? content.event.dateMr : content.event.dateStr}
            </div>
            <div className="px-4 py-2 bg-pink-900/50 border border-pink-400 rounded-full text-sm text-pink-300 font-semibold">
              🕐 {content.event.time}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
